import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Package, RefreshCw, ChevronDown, Plane, Ship, Truck,
  User, Building, MapPin 
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { QuoteStatusBadge, getModeIconStyle, formatDate } from './StatusBadges';

const getModeIcon = (mode) => {
  const icons = { air: Plane, ocean: Ship, road: Truck, multimodal: Package };
  return icons[mode] || Package;
};

export const QuotesList = ({ 
  quotes, 
  loading, 
  pendingCount,
  onQuoteClick 
}) => {
  const { t } = useTranslation();
  
  return (
    <Card>
      <CardHeader className="border-b">
        <CardTitle className="flex items-center justify-between">
          <span>{t('admin.quoteRequests')} ({quotes.length})</span>
          {pendingCount > 0 && (
            <Badge className="bg-amber-100 text-amber-800 animate-pulse">
              {pendingCount} {t('admin.new')}
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="w-8 h-8 text-gold animate-spin" />
          </div>
        ) : quotes.length === 0 ? (
          <div className="text-center py-12">
            <Package className="w-12 h-12 text-steel-300 mx-auto mb-4" />
            <p className="text-steel-500">{t('admin.noQuotes')}</p>
          </div>
        ) : (
          <div className="divide-y divide-steel-100">
            <AnimatePresence>
              {quotes.map((quote, index) => {
                const ModeIcon = getModeIcon(quote.transport_mode);
                const isNew = quote.status === 'pending' && 
                  (new Date() - new Date(quote.created_at)) < 24 * 60 * 60 * 1000;
                
                return (
                  <motion.div
                    key={quote.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ delay: index * 0.05 }}
                    className={`p-4 hover:bg-steel-50 cursor-pointer transition-colors ${isNew ? 'bg-gold/5' : ''}`}
                    onClick={() => onQuoteClick(quote)}
                    data-testid={`quote-row-${quote.id}`}
                  >
                    <div className="flex items-center gap-4">
                      {/* Mode Icon */}
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${getModeIconStyle(quote.transport_mode)}`}>
                        <ModeIcon className="w-5 h-5" />
                      </div>

                      {/* Main Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-semibold text-navy-800">{quote.quote_number}</span>
                          {isNew && (
                            <Badge className="bg-gold text-navy-800 text-xs">{t('admin.new')}</Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-4 text-sm text-steel-500">
                          <span className="flex items-center gap-1">
                            <User className="w-3 h-3" />
                            {quote.contact_name}
                          </span>
                          <span className="flex items-center gap-1">
                            <Building className="w-3 h-3" />
                            {quote.company_name || 'N/A'}
                          </span>
                        </div>
                      </div>

                      {/* Route */}
                      <div className="hidden md:block text-sm">
                        <div className="flex items-center gap-2 text-navy-800">
                          <MapPin className="w-3 h-3 text-gold" />
                          <span>{quote.origin_city}</span>
                          <span className="text-steel-400">→</span>
                          <span>{quote.destination_city}</span>
                        </div>
                      </div>

                      {/* Weight */}
                      <div className="hidden lg:block text-sm text-steel-600">
                        {quote.weight_kg} kg
                      </div>

                      {/* Date */}
                      <div className="hidden md:block text-sm text-steel-500">
                        {formatDate(quote.created_at)}
                      </div>

                      {/* Status */}
                      <div>
                        <QuoteStatusBadge status={quote.status} />
                      </div>

                      {/* Arrow */}
                      <ChevronDown className="w-5 h-5 text-steel-400" />
                    </div>
                  </motion.div>
                );
              })}
            </AnimatePresence>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
